import { useState } from "react";
import { Link } from "react-router-dom";
import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { capacityTiers } from "@/mocks/capacityTiersData";
import {
  blackBeltCourses,
  factorySkillsCourses,
  professionalProficiencyCourses,
} from "@/mocks/capacityCoursesData";
import type { CourseDetail } from "@/mocks/capacityCoursesData";

function SectionHeader({ badge, title, subtitle, dark = false }: {
  badge: string; title: string; subtitle: string; dark?: boolean;
}) {
  return (
    <ScrollReveal>
      <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md mb-4 md:mb-6 ${dark ? "bg-white/10" : "bg-tie-teal/10"}`}>
          <span className={`text-xs font-semibold uppercase tracking-wider ${dark ? "text-tie-teal-light" : "text-tie-teal"}`}>
            / {badge}
          </span>
        </div>
        <h2 className={`font-serif text-2xl md:text-3xl lg:text-5xl font-bold leading-tight ${dark ? "text-white" : "text-tie-text-primary"}`}>
          {title}
        </h2>
        <p className={`mt-3 md:mt-4 text-base md:text-lg ${dark ? "text-white/70" : "text-tie-text-secondary"}`}>
          {subtitle}
        </p>
      </div>
    </ScrollReveal>
  );
}

function CourseCard({ course, index, dark = false }: {
  course: CourseDetail; index: number; dark?: boolean;
}) {
  return (
    <ScrollReveal delay={(index % 6) * 60}>
      <Link
        to={`/training/course/${course.slug}`}
        className="group block h-full"
      >
        <div className={`flex items-start gap-3 p-3.5 md:p-4 rounded-lg border transition-all duration-300 h-full ${
          dark
            ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-tie-teal-light/30"
            : "bg-white border-tie-grey-dark hover:border-tie-teal/30 hover:shadow-sm"
        }`}>
          <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold mt-0.5 ${
            dark ? "bg-tie-teal/20 text-tie-teal-light" : "bg-tie-teal/10 text-tie-teal"
          }`}>
            {index + 1}
          </span>
          <span className={`text-sm font-medium leading-snug ${dark ? "text-white/90" : "text-tie-text-primary"}`}>
            {course.name}
          </span>
          <i className={`ri-arrow-right-line ml-auto shrink-0 text-xs mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${dark ? "text-tie-teal-light" : "text-tie-teal"}`} />
        </div>
      </Link>
    </ScrollReveal>
  );
}

function CourseGrid({ courses, dark = false }: { courses: CourseDetail[]; dark?: boolean }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
      {courses.map((course, i) => (
        <CourseCard key={course.slug} course={course} index={i} dark={dark} />
      ))}
    </div>
  );
}

export default function CapacityTiers() {
  const [activeTeamMember, setActiveTeamMember] = useState(0);
  const bb = capacityTiers.blackBelt;
  const fsd = capacityTiers.factorySkills;
  const pp = capacityTiers.professionalProficiency;

  return (
    <section id="tiers">
      {/* ===== Black Belt — Flagship Programme ===== */}
      <div className="bg-tie-navy py-16 md:py-24 lg:py-32">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              badge="Flagship Certification"
              title={bb.title}
              subtitle={bb.tagline}
              dark
            />

            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10 md:mb-16">
              {[
                { value: `${bb.hoursTraining}+`, label: "Hours of Training" },
                { value: `${bb.hoursProblemSolving}+`, label: "Hours of Problem-Solving" },
                { value: String(blackBeltCourses.length), label: "Specialised Courses" },
                { value: "4-Person", label: "Innovation Team" },
              ].map((stat, i) => (
                <ScrollReveal key={stat.label} delay={i * 80}>
                  <div className="text-center p-5 md:p-6 rounded-xl bg-white/5 border border-white/10">
                    <p className="font-serif text-2xl md:text-3xl font-bold text-white">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-sm text-white/60">{stat.label}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>

            {/* Description */}
            <ScrollReveal>
              <div className="max-w-4xl mx-auto text-center mb-10 md:mb-14">
                <p className="text-base md:text-lg text-white/80 leading-relaxed">
                  {bb.description}
                </p>
              </div>
            </ScrollReveal>

            {/* Team structure + strategy */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-12 md:mb-16">
              {/* Team cards */}
              <ScrollReveal>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-5">
                    {bb.teamStructure.title}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {bb.teamStructure.members.map((m, i) => (
                      <button
                        key={m.role}
                        onClick={() => setActiveTeamMember(i)}
                        className={`text-left p-4 rounded-lg border transition-all duration-300 ${
                          activeTeamMember === i
                            ? "bg-white/10 border-tie-teal-light/50"
                            : "bg-white/5 border-white/10 hover:border-white/20"
                        }`}
                      >
                        <p className="text-sm font-semibold text-white">{m.role}</p>
                        <p className="mt-1 text-xs text-white/50">{m.duty}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </ScrollReveal>

              {/* Strategy note */}
              <ScrollReveal delay={150}>
                <div className="p-5 md:p-6 rounded-xl bg-tie-teal/10 border border-tie-teal/20">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-tie-teal flex items-center justify-center shrink-0">
                      <i className="ri-settings-3-line text-white text-sm" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">Systematic Strategy</p>
                      <p className="mt-1.5 text-sm text-white/70 leading-relaxed">
                        {bb.strategyNote}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            {/* Black Belt Courses */}
            <ScrollReveal>
              <h3 className="text-center text-lg font-semibold text-white mb-6 md:mb-8">
                {bb.coursesHeading}
              </h3>
            </ScrollReveal>
            <CourseGrid courses={blackBeltCourses} dark />
          </div>
        </div>
      </div>

      {/* ===== Factory Skills Development ===== */}
      <div className="bg-tie-grey-light py-16 md:py-24 lg:py-32">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              badge="Factory Skills Development"
              title={fsd.title}
              subtitle={fsd.subtitle}
            />
            <CourseGrid courses={factorySkillsCourses} />
          </div>
        </div>
      </div>

      {/* ===== Professional Proficiency Incubator ===== */}
      <div className="bg-white py-16 md:py-24 lg:py-32">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              badge="Professional Proficiency Incubator"
              title={pp.title}
              subtitle={pp.tagline}
            />

            {/* Narrative */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-10 md:mb-16">
              <div className="space-y-4 md:space-y-5">
                {pp.narrative.slice(0, 2).map((para, i) => (
                  <ScrollReveal key={i} delay={i * 100}>
                    <p className="text-sm md:text-base text-tie-text-secondary leading-relaxed">
                      {para}
                    </p>
                  </ScrollReveal>
                ))}
              </div>
              <div className="space-y-4 md:space-y-5">
                {pp.narrative.slice(2).map((para, i) => (
                  <ScrollReveal key={i} delay={(i + 2) * 100}>
                    <p className="text-sm md:text-base text-tie-text-secondary leading-relaxed">
                      {para}
                    </p>
                  </ScrollReveal>
                ))}
              </div>
            </div>

            {/* KPI note */}
            <ScrollReveal>
              <div className="max-w-3xl mx-auto mb-10 md:mb-14 p-5 md:p-6 rounded-xl bg-tie-navy/5 border border-tie-navy/10">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-tie-navy flex items-center justify-center shrink-0">
                    <i className="ri-bar-chart-grouped-line text-white text-sm" />
                  </div>
                  <p className="text-sm md:text-base text-tie-text-secondary leading-relaxed">
                    <span className="font-semibold text-tie-text-primary">KPI-Driven Outcomes: </span>
                    {pp.kpiNote}
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Courses heading */}
            <ScrollReveal>
              <h3 className="text-center text-lg font-semibold text-tie-text-primary mb-6 md:mb-8">
                {pp.coursesHeading}
              </h3>
            </ScrollReveal>
            <CourseGrid courses={professionalProficiencyCourses} />
          </div>
        </div>
      </div>

      {/* ===== Enrollment CTA ===== */}
      <div className="bg-tie-navy py-16 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-white">
                Ready to Transform Your Career?
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <p className="mt-4 text-base md:text-lg text-white/70 max-w-2xl mx-auto">
                Enrol in a cohort, nominate your team, or speak with our training advisors about the right programme for your factory.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/collaborate"
                  className="inline-flex items-center justify-center px-7 py-3.5 rounded-full bg-tie-teal text-white font-medium text-sm hover:bg-tie-teal-dark transition-all duration-300 whitespace-nowrap"
                >
                  Enrol a Cohort
                  <i className="ri-arrow-right-line ml-2" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-7 py-3.5 rounded-full bg-white/10 text-white border border-white/30 font-medium text-sm hover:bg-white/20 transition-all duration-300 whitespace-nowrap"
                >
                  Speak to an Advisor
                  <i className="ri-customer-service-line ml-2" />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
}